import { createContext, useContext, useEffect, useLayoutEffect, useState, type ReactNode } from 'react';
import { Icon } from './brand';

type Preference = 'system' | 'light' | 'dark';
const STORAGE_KEY = 'si.theme';
const validPreference = (value: string | null): Preference => value === 'light' || value === 'dark' ? value : 'system';
function savedPreference(): Preference {
  try { return validPreference(localStorage.getItem(STORAGE_KEY)); } catch { return 'system'; }
}
const ThemeContext = createContext<{
  preference: Preference;
  theme: 'light' | 'dark';
  choose: (preference: Preference) => void;
} | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreference] = useState(savedPreference);
  const [systemDark, setSystemDark] = useState(() => window.matchMedia('(prefers-color-scheme: dark)').matches);
  const theme = preference === 'system' ? systemDark ? 'dark' : 'light' : preference;
  useLayoutEffect(() => { document.documentElement.dataset.theme = theme; }, [theme]);
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const changed = () => setSystemDark(media.matches);
    const storage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY || e.key === null) setPreference(savedPreference());
    };
    changed();
    media.addEventListener('change', changed);
    window.addEventListener('storage', storage);
    return () => { media.removeEventListener('change', changed); window.removeEventListener('storage', storage); };
  }, []);
  const choose = (value: Preference) => {
    setPreference(value);
    try {
      if (value === 'system') localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, value);
    } catch { /* The switch still works for this visit if storage is blocked. */ }
  };
  return <ThemeContext.Provider value={{ preference, theme, choose }}>{children}</ThemeContext.Provider>;
}

function useTheme() {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('ThemeProvider is missing');
  return value;
}

export function ThemeToggle() {
  const { theme, choose } = useTheme();
  const next = theme === 'dark' ? 'light' : 'dark';
  const label = `Switch to ${next} mode`;
  return (
    <button type="button" className="btn theme-toggle" aria-label={label} title={label} onClick={() => choose(next)}>
      <Icon name={next === 'light' ? 'sun' : 'moon'} />
      <span>{next === 'light' ? 'Light mode' : 'Dark mode'}</span>
    </button>
  );
}

export function ThemeSettings() {
  const { preference, choose } = useTheme();
  return (
    <section className="card" aria-labelledby="appearance-heading">
      <h2 id="appearance-heading">Appearance</h2>
      <p className="muted">Choose how the app looks on this device.</p>
      <div className="row" role="radiogroup" aria-label="Colour theme">
        {([['system', 'Use device setting'], ['light', 'Light'], ['dark', 'Dark']] as const).map(([value, label]) => (
          <label key={value} className={`chip ${preference === value ? 'on' : ''}`}>
            <input type="radio" name="colour-theme" value={value} checked={preference === value} onChange={() => choose(value)} />
            {label}
          </label>
        ))}
      </div>
    </section>
  );
}
